import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { type ReactNode, type Ref, useCallback, useRef } from 'react';

import type { MediaAsset } from '@/assets/media';
import { BLACK_HOLE_SIDE, type ChapterKey, sideSign } from '@/core/chapters';
import { useContent } from '@/content/ContentContext';
import { MOTION_ALLOWED } from '@/motion/queries';
import { cx } from '@/ui/classNames';

import styles from './Chapter.module.css';

interface ChapterProps {
  readonly ref?: Ref<HTMLElement>;
  readonly chapter: ChapterKey;
  readonly id: string;
  readonly className?: string;
  readonly 'aria-label'?: string;
  readonly 'aria-labelledby'?: string;
  /** Sans décor 3D : une image peinte du même univers, à la place de la simple lueur. */
  readonly poster?: MediaAsset;
  readonly children: ReactNode;
}

/**
 * Un chapitre du voyage : une section posée près de son trou noir. Les blocs
 * marqués `data-passage` arrivent du côté opposé au trou noir et repartent de
 * l'autre côté en quittant l'écran : la page avance en zigzag.
 */
export function Chapter({
  ref,
  chapter,
  id,
  className,
  poster,
  children,
  ...labels
}: ChapterProps) {
  const sectionRef = useRef<HTMLElement | null>(null);
  const side = BLACK_HOLE_SIDE[chapter];

  // La section garde sa propre référence et transmet l'élément au parent.
  const attach = useCallback(
    (node: HTMLElement | null) => {
      sectionRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );

  useGSAP(
    () => {
      const section = sectionRef.current;
      if (!section) return;
      // Le texte vient d'en face du trou noir : de la droite si le trou noir est à gauche.
      const from = -sideSign(side);
      const media = gsap.matchMedia();
      media.add(MOTION_ALLOWED, () => {
        gsap.utils.toArray<HTMLElement>('[data-passage]', section).forEach((block) => {
          gsap.fromTo(
            block,
            { xPercent: from * 16, rotateY: from * -9, autoAlpha: 0 },
            {
              xPercent: 0,
              rotateY: 0,
              autoAlpha: 1,
              ease: 'none',
              transformPerspective: 1400,
              scrollTrigger: { trigger: block, start: 'top 96%', end: 'top 52%', scrub: 0.8 },
            },
          );
          gsap.fromTo(
            block,
            { xPercent: 0, autoAlpha: 1 },
            {
              xPercent: -from * 10,
              autoAlpha: 0,
              ease: 'none',
              immediateRender: false,
              scrollTrigger: { trigger: block, start: 'bottom 28%', end: 'bottom -8%', scrub: 0.8 },
            },
          );
        });
      });
      return () => {
        media.revert();
      };
    },
    { scope: sectionRef, dependencies: [side] },
  );

  return (
    <section
      ref={attach}
      id={id}
      className={cx(styles.chapter, className)}
      data-cosmos-chapter={chapter}
      data-side={side}
      {...labels}
    >
      <div
        className={cx(styles.backdrop, poster && styles.painted)}
        aria-hidden="true"
        data-cosmos-decor
      >
        {poster && (
          <img
            src={poster.src}
            width={poster.width}
            height={poster.height}
            alt=""
            loading="lazy"
            decoding="async"
          />
        )}
      </div>
      {children}
    </section>
  );
}

/** La colonne de texte d'un chapitre, posée en face du trou noir. */
export function ChapterColumn({
  chapter,
  className,
  children,
}: {
  readonly chapter: ChapterKey;
  readonly className?: string;
  readonly children: ReactNode;
}) {
  return (
    <div className={cx(styles.column, className)} data-passage>
      <ChapterMark chapter={chapter} />
      {children}
    </div>
  );
}

/** Repère de navigation spatiale : numéro, chapitre, trou noir et coordonnées. */
export function ChapterMark({
  chapter,
  className,
}: {
  readonly chapter: ChapterKey;
  readonly className?: string;
}) {
  const { journey } = useContent();
  const { index, title, star, coordinates } = journey.chapters[chapter];
  return (
    <p className={cx(styles.mark, className)}>
      <span className={styles.markIndex}>{index}</span>
      <span className={styles.markTitle}>{title}</span>
      <span className={styles.markStar} aria-hidden="true">
        {journey.starLabel} {star}
      </span>
      <span className={styles.markCoordinates} aria-hidden="true">
        {coordinates}
      </span>
    </p>
  );
}
